/**
 * Sanitizes a business name for use as a client ID
 * Converts to lowercase, replaces spaces and special characters with hyphens,
 * and ensures the result is a valid identifier
 * 
 * @param businessName - The business name to sanitize
 * @returns A sanitized string safe for use as a client ID
 */
export function sanitizeClientId(businessName: string): string {
  if (!businessName || typeof businessName !== 'string') {
    return 'default';
  }

  // Convert to lowercase
  let id = businessName.toLowerCase().trim();
  
  // Replace spaces and special characters with hyphens
  id = id.replace(/[^a-z0-9]+/g, '-');
  
  // Remove leading and trailing hyphens
  id = id.replace(/^-+|-+$/g, '');
  
  // Ensure it's not empty
  if (!id) {
    return 'default';
  }
  
  // Ensure it's not too long (max 63 chars for many systems)
  if (id.length > 63) {
    id = id.substring(0, 63);
    // Ensure we don't end with a hyphen
    id = id.replace(/-+$/g, '');
  }
  
  return id;
}

/**
 * Extract client ID from URL or fall back to localStorage
 * @returns The client ID or undefined if not found
 */
export const getClientIdFromUrl = (): string | undefined => {
  // Try to get from URL first
  const pathParts = window.location.pathname.split('/');
  const clientIdIndex = pathParts.indexOf('client') + 1;
  
  if (clientIdIndex > 0 && clientIdIndex < pathParts.length) {
    return pathParts[clientIdIndex];
  }
  
  // Fall back to localStorage
  return localStorage.getItem('selected_client_id') || undefined;
}; 